import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import {
  AuthService,
  InvalidPasswordRecoveryError,
  PasswordManagedByProviderError,
  PasswordRecoveryCleanupError,
  RecoveryAuthorization,
} from "../services/authService";

type RecoveryStatus =
  | "exchanging"
  | "ready"
  | "invalid"
  | "provider-managed"
  | "cleanup-error";

const ResetPassword: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const exchangePromiseRef =
    useRef<Promise<RecoveryAuthorization> | null>(null);
  const effectGenerationRef = useRef(0);
  const recoveryCompletedRef = useRef(false);
  const passwordUpdateCompletedRef = useRef(false);
  const [status, setStatus] = useState<RecoveryStatus>("exchanging");
  const [authorization, setAuthorization] =
    useState<RecoveryAuthorization | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const effectGeneration = ++effectGenerationRef.current;
    const callbackHistoryState = window.history.state;

    if (!exchangePromiseRef.current) {
      const code = new URLSearchParams(window.location.search).get("code");
      if (code) {
        const cleanedUrl = new URL(window.location.href);
        cleanedUrl.searchParams.delete("code");
        window.history.replaceState(
          callbackHistoryState,
          "",
          cleanedUrl.pathname + cleanedUrl.search + cleanedUrl.hash
        );
        window.dispatchEvent(
          new PopStateEvent("popstate", { state: callbackHistoryState })
        );
      }

      exchangePromiseRef.current = code
        ? AuthService.exchangePasswordRecoveryCode(code)
        : AuthService.clearPasswordRecoveryAuthorization().then(() => {
            throw new InvalidPasswordRecoveryError();
          });
    }

    const exchangePromise = exchangePromiseRef.current;
    const shouldSkipCleanup = () =>
      effectGenerationRef.current !== effectGeneration ||
      recoveryCompletedRef.current;

    exchangePromise
      .then((recoveryAuthorization) => {
        if (cancelled) return;

        setAuthorization(recoveryAuthorization);
        setStatus("ready");
      })
      .catch((exchangeError) => {
        if (cancelled) return;

        setAuthorization(null);
        if (exchangeError instanceof PasswordRecoveryCleanupError) {
          setStatus("cleanup-error");
        } else if (exchangeError instanceof PasswordManagedByProviderError) {
          setStatus("provider-managed");
        } else {
          setStatus("invalid");
        }
      });

    return () => {
      cancelled = true;

      void Promise.resolve().then(async () => {
        if (shouldSkipCleanup()) {
          return;
        }

        try {
          await exchangePromise;
        } catch {
          // Failed exchanges perform their own cleanup before rejecting.
        }

        if (shouldSkipCleanup()) {
          return;
        }

        try {
          await AuthService.clearPasswordRecoveryAuthorization();
        } catch {
          // The component is gone; storage is still forcibly isolated by the service.
        }
      });
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!authorization) {
      setStatus("invalid");
      return;
    }
    if (newPassword.length < 6) {
      setError(t("reset.passwordLength"));
      return;
    }
    if (newPassword !== confirmation) {
      setError(t("reset.passwordMismatch"));
      return;
    }

    setIsUpdating(true);
    setError("");
    try {
      await AuthService.updateRecoveredPassword(authorization, newPassword);
      passwordUpdateCompletedRef.current = true;
      recoveryCompletedRef.current = true;
      navigate("/?passwordReset=success", { replace: true });
    } catch (updateError) {
      if (updateError instanceof PasswordRecoveryCleanupError) {
        passwordUpdateCompletedRef.current = updateError.passwordUpdated;
        setAuthorization(null);
        setStatus("cleanup-error");
      } else if (updateError instanceof PasswordManagedByProviderError) {
        setAuthorization(null);
        setStatus("provider-managed");
      } else if (updateError instanceof InvalidPasswordRecoveryError) {
        setAuthorization(null);
        setStatus("invalid");
      } else {
        setError(t("reset.updateError"));
      }
      setIsUpdating(false);
    }
  };

  const retryCleanup = async () => {
    setIsCleaning(true);
    try {
      await AuthService.clearPasswordRecoveryAuthorization();
      if (passwordUpdateCompletedRef.current) {
        recoveryCompletedRef.current = true;
        navigate("/?passwordReset=success", { replace: true });
      } else {
        setStatus("invalid");
      }
    } catch {
      setStatus("cleanup-error");
    } finally {
      setIsCleaning(false);
    }
  };

  const renderCard = (content: React.ReactNode) => (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 p-4">
      <section className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl">
        {content}
      </section>
    </main>
  );

  if (status === "exchanging") {
    return renderCard(
      <div className="text-center">
        <div
          className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-purple-600"
          aria-hidden="true"
        />
        <p className="text-gray-600">{t("reset.loading")}</p>
      </div>
    );
  }

  if (status === "cleanup-error") {
    return renderCard(
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("reset.cleanupTitle")}
        </h1>
        <p className="mt-3 text-gray-600">{t("reset.cleanupBody")}</p>
        <button
          type="button"
          onClick={retryCleanup}
          disabled={isCleaning}
          className="mt-6 inline-flex min-h-11 items-center justify-center rounded-lg bg-purple-600 px-5 py-2 font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {t("reset.retryCleanup")}
        </button>
      </div>
    );
  }

  if (status === "invalid") {
    return renderCard(
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("reset.invalidTitle")}
        </h1>
        <p className="mt-3 text-gray-600">{t("reset.invalidBody")}</p>
        <Link
          to="/?mode=forgot-password"
          className="mt-6 inline-flex min-h-11 items-center justify-center rounded-lg bg-purple-600 px-5 py-2 font-medium text-white hover:bg-purple-700"
        >
          {t("reset.requestAnother")}
        </Link>
      </div>
    );
  }

  if (status === "provider-managed") {
    return renderCard(
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {t("reset.googleTitle")}
        </h1>
        <p className="mt-3 text-gray-600">{t("reset.googleBody")}</p>
        <Link
          to="/"
          className="mt-6 inline-flex min-h-11 items-center justify-center rounded-lg bg-purple-600 px-5 py-2 font-medium text-white hover:bg-purple-700"
        >
          {t("login.backToLogin")}
        </Link>
      </div>
    );
  }

  return renderCard(
    <>
      <h1 className="text-2xl font-bold text-gray-900">{t("reset.title")}</h1>
      <p className="mt-2 text-gray-600">{t("reset.subtitle")}</p>

      <form className="mt-6 space-y-5" onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="new-password"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            {t("reset.newPassword")}
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            disabled={isUpdating}
            className="min-h-11 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
        </div>

        <div>
          <label
            htmlFor="confirm-password"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            {t("reset.confirmPassword")}
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            disabled={isUpdating}
            className="min-h-11 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={isUpdating}
          className="min-h-11 w-full rounded-lg bg-purple-600 px-4 py-2 font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isUpdating ? t("reset.updating") : t("reset.update")}
        </button>
      </form>
    </>
  );
};

export default ResetPassword;
